import {
  refreshTiltifyToken as refreshTiltifyTokenViaProxy,
  verifyAuthJWT,
} from "@playlive/fundraiser-auth";
import { resolveApiBaseUri } from "@playlive/fundraiser-auth/config";
import { describeError, trackAppEvent } from "@/util/analytics";
import { configurePlayLiveAuth } from "@/util/auth-config";

declare global {
  interface Window {
    __playlive?: Record<string, unknown>;
  }
}

// Configure at module scope rather than relying solely on `main.tsx`, so that
// any entry point which pulls in this helper directly — unit tests, lazily
// loaded routes — always has the auth singleton wired. `configureAuth` is an
// idempotent replace, so the duplicate call from `main.tsx` is harmless.
configurePlayLiveAuth();

const jwksUrl = new URL("/.well-known/jwks.json", import.meta.env.VITE_TILTIFY_REDIRECT_URI);

/**
 * Verifies a Tiltify-issued JWS token and returns the decoded payload.
 *
 * Delegates to `@playlive/fundraiser-auth`, which fetches the public key from
 * the proxy's `/.well-known/jwks.json` (matched by the `kid` in the JWT header)
 * and memoises the key set per origin.
 *
 * @param {string} jwt - The compact JWS string sent from the Tiltify Lambda.
 * @returns {Promise<{user: Object, profile: Object, tokens: Object}>}
 */
function verifyTiltifyJWT(jwt: string) {
  return verifyAuthJWT<Record<string, unknown>>(jwt, "Tiltify", resolveApiBaseUri("tiltify"));
}

/** Flattens the package's `{ user, profile, tokens }` payload into this app's
 * historical `{ ...user, tokens }` shape. */
function flatten(payload: { user: unknown; tokens: unknown }) {
  return { ...(payload.user as Record<string, unknown>), tokens: payload.tokens };
}

/**
 * Opens a popup window to start the Tiltify OAuth flow.
 * On success, calls `setUser` with `{ ...user, tokens }` where tokens contains
 * `accessToken`, `refreshToken`, `expiresIn`, and `createdAt`.
 *
 * NOTE: this deliberately keeps a local message handler rather than using
 * `openTiltifyOAuthPopup` from `@playlive/fundraiser-auth`. The package only
 * understands the current raw-JWT `postMessage` shape, whereas this app is
 * still reachable by two legacy Lambda response shapes (a plain user object,
 * and a JSON-wrapped `{ jwt }`). Only the verification plumbing is delegated.
 *
 * @param {function({id: string, username: string, slug: string, tokens: Object}): void} setUser
 */
export function openTiltifyOAuthPopup(
  setUser: (user: Record<string, unknown>) => void = (_user) => {},
) {
  const url = new URL("https://v5api.tiltify.com/oauth/authorize");
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", import.meta.env.VITE_TILTIFY_CLIENT_ID);
  url.searchParams.set("redirect_uri", encodeURI(import.meta.env.VITE_TILTIFY_REDIRECT_URI));
  url.searchParams.set("scope", "public");

  const origin = new URL(import.meta.env.VITE_TILTIFY_REDIRECT_URI).origin;

  const win = window.open(url.toString());
  win?.focus();

  const handleEvent = (event: MessageEvent) => {
    // biome-ignore lint/suspicious/noConsole: popup message debug
    console.debug(
      "[Tiltify] message event — origin:",
      event.origin,
      "expected:",
      origin,
      "data preview:",
      String(event.data).slice(0, 80),
    );

    if (event.origin !== origin) {
      // biome-ignore lint/suspicious/noConsole: popup message debug
      console.debug("[Tiltify] Ignoring message from unexpected origin:", event.origin);
      return;
    }

    try {
      const data = JSON.parse(event.data);
      // biome-ignore lint/suspicious/noConsole: popup message debug
      console.debug("[Tiltify] Parsed JSON message, keys:", Object.keys(data));

      if (data.id) {
        // Legacy path: Lambda sent a plain JSON object with an `id` field.
        // biome-ignore lint/suspicious/noConsole: popup message debug
        console.debug("[Tiltify] Legacy path — plain user object");
        trackAppEvent({ eventName: "tiltify_oauth", eventAction: "success", authPath: "legacy" });
        setUser(data);
        window.removeEventListener("message", handleEvent);
      } else if (data.jwt) {
        // Current path: Lambda sent JSON containing a signed JWT.
        // biome-ignore lint/suspicious/noConsole: popup message debug
        console.debug("[Tiltify] JSON-wrapped JWT path");
        verifyTiltifyJWT(data.jwt)
          .then((payload) => {
            trackAppEvent({
              eventName: "tiltify_oauth",
              eventAction: "success",
              authPath: "json-jwt",
            });
            setUser(flatten(payload));
            window.removeEventListener("message", handleEvent);
          })
          .catch((err) => {
            // biome-ignore lint/suspicious/noConsole: error logging for JWT verification failures
            console.error("[Tiltify] Failed to verify JWT from JSON message:", err);
            trackAppEvent({
              eventName: "tiltify_oauth",
              eventAction: "failure",
              authPath: "json-jwt",
              failureReason: "jwt_verification_failed",
              errorMessage: describeError(err),
            });
          });
      } else {
        // biome-ignore lint/suspicious/noConsole: popup message debug
        console.warn("[Tiltify] JSON message had neither `id` nor `jwt`:", data);
        trackAppEvent({
          eventName: "tiltify_oauth",
          eventAction: "failure",
          failureReason: "unrecognized_message_shape",
        });
      }
    } catch (_err) {
      // Current path: Lambda sent a raw JWT string (not JSON-wrapped).
      // biome-ignore lint/suspicious/noConsole: popup message debug
      console.debug("[Tiltify] Non-JSON message — treating as raw JWT string");
      verifyTiltifyJWT(event.data)
        .then((payload) => {
          trackAppEvent({
            eventName: "tiltify_oauth",
            eventAction: "success",
            authPath: "raw-jwt",
          });
          setUser(flatten(payload));
          window.removeEventListener("message", handleEvent);
        })
        .catch((err) => {
          // biome-ignore lint/suspicious/noConsole: error logging for JWT verification failures
          console.error("[Tiltify] Failed to verify raw JWT:", err);
          trackAppEvent({
            eventName: "tiltify_oauth",
            eventAction: "failure",
            authPath: "raw-jwt",
            failureReason: "jwt_verification_failed",
            errorMessage: describeError(err),
          });
        });
    }
  };

  window.addEventListener("message", handleEvent);
}

/**
 * Returns `true` when the Tiltify access token has expired or will expire within
 * the given buffer window.
 *
 * @param {string|null|undefined} createdAt   - ISO-8601 timestamp from `tokens.createdAt`
 * @param {string|number|null|undefined} expiresIn - Lifetime in seconds from `tokens.expiresIn`
 * @param {number} [bufferSeconds=300]        - Seconds before actual expiry to treat as expired
 * @returns {boolean}
 */
export function isTiltifyTokenExpired(
  createdAt: string | null | undefined,
  expiresIn: string | number | null | undefined,
  bufferSeconds = 300,
) {
  if (!createdAt || !expiresIn) {
    // Can't determine expiry — assume expired so a refresh is attempted.
    return true;
  }
  const expiresAt = new Date(createdAt).getTime() + Number(expiresIn) * 1000;
  return Date.now() >= expiresAt - bufferSeconds * 1000;
}

/**
 * Calls the Tiltify Lambda refresh endpoint to exchange a refresh token for a
 * new access token, via `@playlive/fundraiser-auth`. The endpoint origin comes
 * from the configured `tiltifyApiBaseUri` (see `@/util/auth-config`).
 *
 * Concurrent calls with the same `refreshToken` share a single upstream
 * exchange. That de-duplication lives in `@playlive/fundraiser-auth` (>=0.4.0)
 * rather than here: Tiltify **rotates** refresh tokens, so a duplicate
 * in-flight exchange comes back `invalid_grant` (HTTP 400) and poisons the
 * stored session — a property of the OAuth server, not of this app's render
 * discipline.
 *
 * Resolves with `{ ...user, tokens }` on success, or `null` when the refresh
 * fails (network error, invalid refresh token, unexpected response shape, etc.)
 * — the package throws in those cases, and this adapter keeps the app's
 * historical null-on-failure contract.
 *
 * @param {string} refreshToken
 * @returns {Promise<{id: string, username: string, slug: string, tokens: Object}|null>}
 */
export function refreshTiltifyToken(refreshToken: string) {
  return refreshTiltifyTokenViaProxy(refreshToken)
    .then(flatten)
    .catch((err) => {
      // biome-ignore lint/suspicious/noConsole: error logging for token refresh failures
      console.error(
        "[Tiltify] Token refresh failed — base URI was:",
        resolveApiBaseUri("tiltify"),
        "| Error:",
        err?.message ?? err,
      );
      // A failed refresh silently logs the user out mid-session, so it is worth
      // reporting even though no click precedes it.
      trackAppEvent({
        eventName: "tiltify_token_refresh",
        eventAction: "failure",
        errorMessage: describeError(err),
      });
      return null;
    });
}

// ---------------------------------------------------------------------------
// Console debug utilities — exposes helpers on window.__playlive.tiltify so
// you can manually test from the browser console, e.g.:
//   window.__playlive.tiltify.refreshToken("myRefreshToken").then(console.log)
//   window.__playlive.tiltify.verifyJWT("eyJ...").then(console.log)
// ---------------------------------------------------------------------------
if (typeof window !== "undefined") {
  window.__playlive = window.__playlive ?? {};
  window.__playlive.tiltify = {
    /**
     * Manually trigger a token refresh.
     * @param {string} refreshToken
     * @returns {Promise<Object|null>}
     */
    refreshToken: (refreshToken: string) => refreshTiltifyToken(refreshToken),
    /**
     * Manually verify a raw JWT string.
     * @param {string} jwt
     * @returns {Promise<Object>}
     */
    verifyJWT: (jwt: string) => verifyTiltifyJWT(jwt),
    /**
     * Log the JWKS URL that was computed at module load time.
     */
    jwksUrl: jwksUrl.toString(),
  };
}
